import { apiRequest } from './client'
import { readInt, readString } from './json'
import type {
  CatalogFilterOptionsDto,
  CatalogItemDto,
  CatalogItemFieldsUpdate,
  CatalogItemsPageDto,
  CatalogSummaryDto,
} from './catalogTypes'

const API_PREFIX = '/buyer/private-catalog'

type Raw = Record<string, unknown>

const normalizeSummary = (json: Raw): CatalogSummaryDto => ({
  totalItems: Number(json.total_items ?? json.totalItems ?? 0),
  matchAuto: Number(json.match_auto ?? json.matchAuto ?? 0),
  matchSuggested: Number(json.match_suggested ?? json.matchSuggested ?? 0),
  matchManual: Number(json.match_manual ?? json.matchManual ?? 0),
  matchUnmatched: Number(json.match_unmatched ?? json.matchUnmatched ?? 0),
  needsAttention: Number(json.needs_attention ?? json.needsAttention ?? 0),
  staleItems: Number(json.stale_items ?? json.staleItems ?? 0),
  hiddenFromSearch: Number(json.hidden_from_search ?? json.hiddenFromSearch ?? 0),
})

const normalizeItem = (json: Raw): CatalogItemDto => ({
  id: Number(json.id ?? 0),
  organizationId: Number(json.organizationId ?? json.organization_id ?? 0),
  positionName: String(
    json.name
    ?? json.item_name
    ?? json.position_name
    ?? json.supplierName
    ?? json.supplier_name
    ?? '',
  ),
  supplierName: String(json.supplierName ?? json.supplier_name ?? ''),
  matchStatus: String(json.matchStatus ?? json.match_status ?? ''),
  externalSku: (json.externalSku as string | null) ?? (json.external_sku as string | null) ?? null,
  brand: (json.brand as string | null) ?? null,
  country: (json.country as string | null) ?? null,
  unit: (json.unit as string | null) ?? null,
  price: json.price != null ? Number(json.price) : null,
  minShipmentQty:
    json.minShipmentQty != null
      ? Number(json.minShipmentQty)
      : json.min_shipment_qty != null
        ? Number(json.min_shipment_qty)
        : null,
  packageSize: (json.packageSize as string | null) ?? (json.package_size as string | null) ?? null,
  productTypeId:
    json.productTypeId != null
      ? Number(json.productTypeId)
      : json.product_type_id != null
        ? Number(json.product_type_id)
        : null,
  productTypeCode: (json.productTypeCode as string | null) ?? (json.product_type_code as string | null) ?? null,
  productTypeName: (json.productTypeName as string | null) ?? (json.product_type_name as string | null) ?? null,
  productTypeActive:
    json.productTypeActive != null
      ? Boolean(json.productTypeActive)
      : json.product_type_active != null
        ? Boolean(json.product_type_active)
        : null,
  matchConfidence:
    json.matchConfidence != null
      ? Number(json.matchConfidence)
      : json.match_confidence != null
        ? Number(json.match_confidence)
        : null,
  operatorComment: (json.operatorComment as string | null) ?? (json.operator_comment as string | null) ?? null,
  updatedAt: (json.updatedAt as string | null) ?? (json.updated_at as string | null) ?? null,
  freshnessStatus: (() => {
    const raw = (json.freshnessStatus as string | null) ?? (json.freshness_status as string | null) ?? null
    if (raw === 'fresh' || raw === 'stale_7' || raw === 'stale_14' || raw === 'stale_30') {
      return raw
    }
    return null
  })(),
  daysSinceUpdate:
    json.daysSinceUpdate != null
      ? Number(json.daysSinceUpdate)
      : json.days_since_update != null
        ? Number(json.days_since_update)
        : null,
  buyerSearchVisible:
    json.buyerSearchVisible != null
      ? Boolean(json.buyerSearchVisible)
      : json.buyer_search_visible != null
        ? Boolean(json.buyer_search_visible)
        : null,
  privateSupplierId:
    json.privateSupplierId != null
      ? Number(json.privateSupplierId)
      : json.private_supplier_id != null
        ? Number(json.private_supplier_id)
        : null,
  privateSupplierName:
    (json.privateSupplierName as string | null) ??
    (json.private_supplier_name as string | null) ??
    null,
  previousPrice:
    json.previousPrice != null
      ? Number(json.previousPrice)
      : json.previous_price != null
        ? Number(json.previous_price)
        : null,
  priceDeltaPct:
    json.priceDeltaPct != null
      ? Number(json.priceDeltaPct)
      : json.price_delta_pct != null
        ? Number(json.price_delta_pct)
        : null,
  previousObservedAt:
    (json.previousObservedAt as string | null) ??
    (json.previous_observed_at as string | null) ??
    null,
})

const normalizePage = (json: Raw): CatalogItemsPageDto => ({
  items: ((json.items as Raw[] | undefined) ?? []).map(normalizeItem),
  page: Number(json.page ?? 0),
  size: Number(json.size ?? 0),
  totalElements: Number(json.totalElements ?? json.total_elements ?? 0),
  totalPages: Number(json.totalPages ?? json.total_pages ?? 0),
})

export const getRestaurantMyPricesSummary = async (
  organizationId: number,
): Promise<CatalogSummaryDto> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/summary?organizationId=${organizationId}`)
  return normalizeSummary(result)
}

export const listRestaurantMyPricesItems = async (options: {
  organizationId: number
  page?: number
  size?: number
  matchStatus?: string
  productTypeId?: number | null
  /** Все id выбранной ветки (родитель + потомки) — для demo и будущего API. */
  productTypeIds?: number[]
  unassignedProductType?: boolean
  search?: string
  privateSupplierId?: number | null
}): Promise<CatalogItemsPageDto> => {
  const params = new URLSearchParams({
    organizationId: String(options.organizationId),
    page: String(options.page ?? 0),
    size: String(options.size ?? 50),
  })
  if (options.matchStatus) {
    params.set('matchStatus', options.matchStatus)
  }
  if (options.productTypeId != null) {
    params.set('productTypeId', String(options.productTypeId))
  }
  if (options.unassignedProductType) {
    params.set('unassignedProductType', 'true')
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim())
  }
  if (options.privateSupplierId != null) {
    params.set('private_supplier_id', String(options.privateSupplierId))
  }
  const result = await apiRequest<Raw>(`${API_PREFIX}/items?${params.toString()}`)
  return normalizePage(result)
}

export const listRestaurantMyPricesFilterOptions = async (
  organizationId: number,
): Promise<CatalogFilterOptionsDto> => {
  const response = await apiRequest<Raw>(`${API_PREFIX}/filter-options?organizationId=${organizationId}`)
  return {
    brands: (response.brands as string[] | undefined) ?? [],
    countries: (response.countries as string[] | undefined) ?? [],
  }
}

export const updateRestaurantMyPricesItemFields = async (
  itemId: number,
  update: CatalogItemFieldsUpdate,
): Promise<CatalogItemDto> => {
  const body: Record<string, unknown> = {}
  if (update.name !== undefined) body.name = update.name
  if (update.brand !== undefined) body.brand = update.brand
  if (update.price !== undefined) body.price = update.price
  if (update.country !== undefined) body.country = update.country
  if (update.unit !== undefined) body.unit = update.unit
  if (update.minShipmentQty !== undefined) body.min_shipment_qty = update.minShipmentQty
  if (update.packageSize !== undefined) body.package_size = update.packageSize
  if (update.comment !== undefined) body.comment = update.comment

  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/fields`, {
    method: 'PATCH',
    body,
  })
  return normalizeItem(result)
}

export const assignRestaurantMyPricesProductType = async (
  itemId: number,
  productTypeId: number,
): Promise<CatalogItemDto> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/product-type`, {
    method: 'PATCH',
    body: { product_type_id: productTypeId },
  })
  return normalizeItem(result)
}

export const confirmRestaurantMyPricesSuggestion = async (
  itemId: number,
): Promise<CatalogItemDto> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}/confirm-suggestion`, {
    method: 'POST',
  })
  return normalizeItem(result)
}

export const deleteRestaurantMyPricesItem = async (itemId: number): Promise<number> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/${itemId}`, { method: 'DELETE' })
  return Number(result.deleted ?? 0)
}

export const bulkDeleteRestaurantMyPricesItems = async (options: {
  organizationId: number
  itemIds?: number[]
  deleteAll?: boolean
}): Promise<number> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/items/bulk-delete`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      ...(options.itemIds?.length ? { item_ids: options.itemIds } : {}),
      delete_all: options.deleteAll ?? false,
    },
  })
  return Number(result.deleted ?? 0)
}

export type RestaurantPrivateSupplierDto = {
  id: number
  legalName: string
  inn: string
  displayLabel: string | null
  linkedSupplierOrganizationId: number | null
  isRegisteredSupplier: boolean
}

export type RestaurantPrivateSupplierStatsDto = {
  privateSupplierId: number
  itemCount: number
  lastUpdatedAt: string | null
  freshnessStatus: CatalogItemDto['freshnessStatus']
}

export type RestaurantPrivateSupplierMatchDto = {
  linkedSupplierOrganizationId: number | null
  linkedSupplierName: string | null
  existingPrivateSupplierId: number | null
}

const normalizePrivateSupplier = (json: Raw): RestaurantPrivateSupplierDto => ({
  id: Number(json.id ?? 0),
  legalName: String(json.legal_name ?? json.legalName ?? ''),
  inn: String(json.inn ?? ''),
  displayLabel: (json.display_label as string | null) ?? (json.displayLabel as string | null) ?? null,
  linkedSupplierOrganizationId:
    json.linked_supplier_organization_id != null
      ? Number(json.linked_supplier_organization_id)
      : json.linkedSupplierOrganizationId != null
        ? Number(json.linkedSupplierOrganizationId)
        : null,
  isRegisteredSupplier:
    json.is_registered_supplier != null
      ? Boolean(json.is_registered_supplier)
      : Boolean(json.isRegisteredSupplier),
})

const normalizePrivateSupplierStats = (json: Raw): RestaurantPrivateSupplierStatsDto => {
  const rawFreshness =
    (json.freshness_status as string | null) ??
    (json.freshnessStatus as string | null) ??
    null
  const freshnessStatus: CatalogItemDto['freshnessStatus'] =
    rawFreshness === 'fresh'
    || rawFreshness === 'stale_7'
    || rawFreshness === 'stale_14'
    || rawFreshness === 'stale_30'
      ? rawFreshness
      : null

  return {
    privateSupplierId: Number(json.private_supplier_id ?? json.privateSupplierId ?? 0),
    itemCount: Number(json.items_total ?? json.item_count ?? json.itemCount ?? 0),
    lastUpdatedAt:
      (json.last_updated_at as string | null) ??
      (json.lastUpdatedAt as string | null) ??
      null,
    freshnessStatus,
  }
}

export const listRestaurantPrivateSuppliers = async (
  organizationId: number,
  venueId?: number | null,
): Promise<RestaurantPrivateSupplierDto[]> => {
  const params = new URLSearchParams({ organizationId: String(organizationId) })
  if (venueId != null) {
    params.set('venue_id', String(venueId))
  }
  const result = await apiRequest<Raw[]>(`${API_PREFIX}/suppliers?${params.toString()}`)
  return (result ?? []).map((entry) => normalizePrivateSupplier(entry as Raw))
}

export const listRestaurantPrivateSupplierStats = async (
  organizationId: number,
): Promise<RestaurantPrivateSupplierStatsDto[]> => {
  const result = await apiRequest<Raw[]>(`${API_PREFIX}/suppliers/stats?organizationId=${organizationId}`)
  return (result ?? []).map((entry) => normalizePrivateSupplierStats(entry as Raw))
}

export const matchRestaurantPrivateSupplier = async (
  organizationId: number,
  inn: string,
  legalName?: string,
): Promise<RestaurantPrivateSupplierMatchDto> => {
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    inn: inn.trim(),
  })
  if (legalName?.trim()) {
    params.set('legalName', legalName.trim())
  }
  const json = await apiRequest<Raw>(`${API_PREFIX}/suppliers/match?${params.toString()}`)
  return {
    linkedSupplierOrganizationId:
      json.linked_supplier_organization_id != null
        ? Number(json.linked_supplier_organization_id)
        : json.linkedSupplierOrganizationId != null
          ? Number(json.linkedSupplierOrganizationId)
          : null,
    linkedSupplierName:
      (json.linked_supplier_name as string | null) ??
      (json.linkedSupplierName as string | null) ??
      null,
    existingPrivateSupplierId:
      json.existing_private_supplier_id != null
        ? Number(json.existing_private_supplier_id)
        : json.existingPrivateSupplierId != null
          ? Number(json.existingPrivateSupplierId)
          : null,
  }
}

type CatalogDuplicateMatchReason = 'EXTERNAL_SKU' | 'IMPORT_SOURCE_NAME' | 'NAME_SIMILAR'

export type MyPricesDuplicateGroupDto = {
  matchReason: CatalogDuplicateMatchReason
  matchKey: string
  items: CatalogItemDto[]
}

export type MyPricesDuplicateGroupsResponse = {
  organizationId: number
  groups: MyPricesDuplicateGroupDto[]
}

export type MergeMyPricesDuplicatesResult = {
  keptItem: CatalogItemDto
  mergedCount: number
}

const normalizeDuplicateReason = (raw: string | null): CatalogDuplicateMatchReason => {
  if (raw === 'EXTERNAL_SKU' || raw === 'IMPORT_SOURCE_NAME' || raw === 'NAME_SIMILAR') {
    return raw
  }
  return 'NAME_SIMILAR'
}

export const listMyPricesDuplicates = async (
  organizationId: number,
): Promise<MyPricesDuplicateGroupsResponse> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/duplicates?organizationId=${organizationId}`)
  const groups = ((result.groups as Raw[] | undefined) ?? []).map((group) => ({
    matchReason: normalizeDuplicateReason(readString(group, 'matchReason', 'match_reason')),
    matchKey: readString(group, 'matchKey', 'match_key') ?? '',
    items: ((group.items as Raw[] | undefined) ?? []).map(normalizeItem),
  }))
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    groups,
  }
}

export const mergeMyPricesDuplicates = async (options: {
  organizationId: number
  keepItemId: number
  mergeItemIds: number[]
  name?: string | null
  brand?: string | null
  price?: number | null
  country?: string | null
  unit?: string | null
  externalSku?: string | null
}): Promise<MergeMyPricesDuplicatesResult> => {
  const body: Record<string, unknown> = {
    organization_id: options.organizationId,
    keep_item_id: options.keepItemId,
    merge_item_ids: options.mergeItemIds,
  }
  if (options.name !== undefined) body.name = options.name
  if (options.brand !== undefined) body.brand = options.brand
  if (options.price !== undefined) body.price = options.price
  if (options.country !== undefined) body.country = options.country
  if (options.unit !== undefined) body.unit = options.unit
  if (options.externalSku !== undefined) body.external_sku = options.externalSku

  const result = await apiRequest<Raw>(`${API_PREFIX}/duplicates/merge`, {
    method: 'POST',
    body,
  })
  const keptRaw = (result.kept_item as Raw | undefined) ?? (result.keptItem as Raw | undefined) ?? {}
  return {
    keptItem: normalizeItem(keptRaw),
    mergedCount: Number(result.merged_count ?? result.mergedCount ?? 0),
  }
}
